import { useMemo, useState } from "react"
import { Link, router } from "@inertiajs/react"
import {
  BookOpen,
  Check,
  Clock,
  FileText,
  Image as ImageIcon,
  RotateCcw,
  ShieldCheck,
} from "lucide-react"

import AppShell from "@/components/tuw/AppShell"
import { Avatar } from "@/components/tuw/Avatar"
import SortDropdown from "@/components/tuw/hire/SortDropdown"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { postOrderAction } from "@/lib/orderActions"
import { cn } from "@/lib/utils"

// kind icon -> Lucide glyph (matches the prototype's per-kind icon).
const KIND_ICON = { file: FileText, shield: ShieldCheck, image: ImageIcon, book: BookOpen }

// The filter chips (client-side, by category). Each carries its live count.
const CHIPS = [
  { key: "all", label: "All" },
  { key: "manuscripts", label: "Manuscripts" },
  { key: "designs", label: "Designs" },
  { key: "illustrations", label: "Illustrations" },
  { key: "reports", label: "Reports" },
]

// Per-kind confirm copy for the inline Approve dialog — mirrors the order-detail
// approve modals (each approval has a real, sometimes irreversible, effect).
const CONFIRM = {
  "Draft approval": {
    title: "Approve this draft?",
    body: "Approving locks the manuscript and moves it into originality review and editing. You cannot request content changes after approval. If the draft needs another pass, cancel and request changes now.",
  },
  "Originality report": {
    title: "Approve the originality report?",
    body: "Your project continues into editing. If anything looks off, send it back to your writer instead.",
  },
  "Design approval": {
    title: "Approve the artwork?",
    body: "This finalizes the illustration set. Request revisions instead if anything needs another pass.",
  },
  "Final approval": {
    title: "Approve the finished deliverable?",
    body: "Approving marks this project complete. Requesting changes instead uses one of your revisions.",
  },
}

const SAMPLE_CONFIRM = {
  "Draft approval": {
    title: "Approve this sample draft?",
    body: "Your sample moves into originality review. After you approve the report, the sample is complete.",
  },
  "Originality report": {
    title: "Approve the sample's originality report?",
    body: "Approving completes this sample. It does not move into editing.",
  },
}

function confirmCopy(item) {
  if (item?.approvalFlow === "sample" && SAMPLE_CONFIRM[item.kind]) {
    return SAMPLE_CONFIRM[item.kind]
  }

  return CONFIRM[item?.kind]
}

// The little 64px thumb on the left of a card: a book spine for final
// manuscripts, a striped placeholder for design/illustration, an icon tile
// otherwise (mirrors approvals.html `thumb()`).
function Thumb({ item }) {
  const Icon = KIND_ICON[item.icon] ?? FileText

  if (item.icon === "book") {
    return (
      <div className="flex h-20 w-16 shrink-0 flex-col justify-center overflow-hidden rounded-[9px] border border-border bg-[linear-gradient(150deg,#2b2b3a,#3a3550)] p-2">
        <div className="font-display text-[8.5px] leading-[1.15] font-bold text-white">{item.project}</div>
        <div className="mt-1 text-[6px] tracking-[0.1em] text-white/60 uppercase">{item.role}</div>
      </div>
    )
  }

  if (item.icon === "image") {
    return (
      <div className="grid h-20 w-16 shrink-0 place-items-center rounded-[9px] border border-border bg-[image:repeating-linear-gradient(135deg,var(--secondary),var(--secondary)_6px,var(--accent)_6px,var(--accent)_12px)]">
        <ImageIcon className="size-[22px] text-ink-3" strokeWidth={1.6} />
      </div>
    )
  }

  return (
    <div className="grid size-16 shrink-0 place-items-center rounded-[12px] border border-border bg-secondary">
      <Icon className="size-6 text-muted-foreground" strokeWidth={1.7} />
    </div>
  )
}

// The due chip — real auto-approval countdown (urgent ≤ 24h, soon ≤ 48h).
function DueChip({ due }) {
  if (!due) return null
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-[11px] py-[5px] text-[11.5px] font-semibold whitespace-nowrap",
        due.tone === "urgent"
          ? "bg-[var(--red-soft)] text-primary"
          : due.tone === "soon"
            ? "bg-needs-you/15 text-needs-you"
            : "bg-secondary text-muted-foreground"
      )}
    >
      <Clock className="size-[13px]" strokeWidth={2} />
      {due.label}
    </span>
  )
}

function ApprovalCard({ item, onApprove }) {
  const urgent = item.due?.tone === "urgent"

  return (
    // MOBILE-PLAN :67 — ≤640 the action cluster stacks and wraps (prototype
    // `.acard{padding:14px;gap:14px}` + `.aright`/`.aacts`). Tailwind v4
    // compiles `max-[N]` EXCLUSIVE, so "≤640" is `max-[641px]`.
    <div
      className={cn(
        "grid grid-cols-[64px_1fr] items-center gap-[18px] rounded-2xl border border-border bg-card p-[18px] transition-[border-color,box-shadow] max-[641px]:gap-3.5 max-[641px]:p-3.5 hover:border-line-2 hover:shadow-[0_1px_3px_rgba(0,0,0,0.04)] lg:grid-cols-[64px_1fr_auto]",
        urgent && "border-l-[3px] border-l-primary"
      )}
      data-testid="approval-card"
    >
      <Thumb item={item} />

      <div className="min-w-0" data-testid="approval-text">
        <div className="mb-[5px] inline-flex items-center gap-1.5 text-[10.5px] font-bold tracking-[0.06em] text-ink-3 uppercase">
          {(() => {
            const Icon = KIND_ICON[item.icon] ?? FileText
            return <Icon className="size-3" strokeWidth={2} />
          })()}
          {item.kind}
        </div>
        <h3 className="font-display text-[15.5px] leading-tight font-semibold tracking-[-0.2px]">{item.title}</h3>
        <div className="mt-[3px] flex flex-wrap items-center gap-[7px] text-[12px] text-ink-3">
          <span>{item.project}</span>
          <span className="text-line-2">·</span>
          <span className="font-display">#{item.order}</span>
          {item.who && (
            <>
              <span className="text-line-2">·</span>
              <span className="flex items-center gap-1.5">
                <Avatar initials={item.who.initials} name={item.who.name} size={18} />
                {item.role}
              </span>
            </>
          )}
        </div>
        <p className="mt-[7px] max-w-[54ch] text-[12.5px] leading-[1.5] text-muted-foreground">{item.desc}</p>
      </div>

      {/* The three buttons are `whitespace-nowrap`, so before the wrap their
          combined min-content (356px) was the MINIMUM size of the card's `1fr`
          track: at a 365 viewport the track measured 469px and `main`
          overflowed by 231px, carrying Request revision and Approve clean off
          the right edge. Wrapping releases the track to the widest single
          button. */}
      <div
        className="col-start-2 flex w-full items-center justify-between gap-3 max-[641px]:flex-col max-[641px]:items-start max-[641px]:gap-2.5 lg:col-start-3 lg:w-auto lg:flex-col lg:items-end lg:gap-[11px]"
        data-testid="approval-actions"
      >
        <DueChip due={item.due} />
        <div className="flex items-center gap-2 max-[641px]:w-full max-[641px]:flex-wrap" data-testid="approval-buttons">
          <Button asChild variant="outline" size="sm" className="max-[641px]:flex-auto">
            <Link href={item.reviewHref}>Review</Link>
          </Button>
          {!item.noRevise && (
            <Button asChild variant="outline" size="sm" className="gap-1.5 max-[641px]:flex-auto">
              <Link href={item.reviewHref}>
                <RotateCcw className="size-3.5" strokeWidth={2} />
                Request revision
              </Link>
            </Button>
          )}
          {item.canApproveInline ? (
            <Button
              size="sm"
              className="gap-1.5 font-semibold max-[641px]:flex-auto"
              onClick={() => onApprove(item)}
            >
              <Check className="size-3.5" strokeWidth={2.4} />
              Approve
            </Button>
          ) : (
            <Button asChild size="sm" className="gap-1.5 font-semibold max-[641px]:flex-auto">
              <Link href={item.reviewHref}>
                <Check className="size-3.5" strokeWidth={2.4} />
                Approve
              </Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

function Group({ label, count, due, children }) {
  return (
    <div className="flex flex-col gap-[11px]">
      <div className="mt-1.5 mb-px flex items-center gap-[9px] px-0.5 text-[11px] font-bold tracking-[0.08em] text-ink-3 uppercase">
        {label}
        <span
          className={cn(
            "inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] tracking-normal",
            due ? "bg-[var(--red-soft)] text-primary" : "bg-secondary text-muted-foreground"
          )}
        >
          {count}
        </span>
      </div>
      {children}
    </div>
  )
}

// Atrium Stage 5 — the cross-project Approvals queue. One prioritized list of
// everything awaiting the customer (drafts, originality reports, designs,
// illustrations, finished deliverables). Due sorting groups real auto-approval
// deadlines; newest sorting preserves the server's submission order. Review /
// Request revision deep-link into the order;
// Approve posts to the same OrderActions endpoints the order-detail modals use,
// then returns here with the item cleared.
export default function Approvals({ approvals }) {
  const { items, counts, urgentCount, sort = "due", sorts = [] } = approvals
  const [filter, setFilter] = useState("all")
  const [confirm, setConfirm] = useState(null) // the item pending Approve confirmation
  const [submitting, setSubmitting] = useState(false)

  const visible = useMemo(
    () => (filter === "all" ? items : items.filter((item) => item.category === filter)),
    [items, filter]
  )

  const soon = visible.filter((item) => item.due?.soon)
  const later = visible.filter((item) => !item.due?.soon)

  const approve = (item) => {
    setSubmitting(true)
    postOrderAction(item.orderId, item.approvePath, { approved: true, from: "approvals" }, {
      onDone: () => {
        setSubmitting(false)
        setConfirm(null)
      },
    })
  }

  const changeSort = (nextSort) => {
    const query = nextSort === "due" ? {} : { sort: nextSort }
    router.get("/approvals", query, {
      only: ["approvals"],
      preserveScroll: true,
      preserveState: true,
      replace: true,
    })
  }

  const subtitle =
    items.length === 0
      ? "Nothing is waiting on you right now."
      : `${items.length} item${items.length === 1 ? "" : "s"} waiting on you` +
        (urgentCount > 0 ? ` · ${urgentCount} due within 24 hours` : "")

  return (
    <>
      <div className="flex flex-col gap-5 px-[26px] pt-6 pb-12">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-[-0.4px]">Approvals</h1>
            <p className="mt-1 text-[13px] text-ink-3">{subtitle}</p>
          </div>
        </div>

        {items.length > 0 && (
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-[7px]">
              {CHIPS.filter((chip) => chip.key === "all" || counts[chip.key] > 0).map((chip) => {
                const active = filter === chip.key
                const n = counts[chip.key]
                return (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => setFilter(chip.key)}
                    className={cn(
                      "flex items-center gap-[7px] rounded-[9px] border px-[13px] py-[7px] text-[12.5px] font-semibold whitespace-nowrap transition-colors",
                      active
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card text-muted-foreground hover:bg-secondary"
                    )}
                  >
                    {chip.label}
                    <span className={cn("text-[11px] font-bold", active ? "text-white/80" : "text-ink-3")}>{n}</span>
                  </button>
                )
              })}
            </div>
            <div className="ml-auto">
              <SortDropdown value={sort} options={sorts} onPick={changeSort} />
            </div>
          </div>
        )}

        {items.length === 0 ? (
          <div className="grid place-items-center rounded-2xl border border-border bg-card py-20 text-center">
            <Check className="size-9 text-done" strokeWidth={1.6} />
            <h3 className="mt-4 font-display text-base font-bold">You're all caught up on approvals</h3>
            <p className="mt-1.5 max-w-[360px] text-[13px] text-ink-3">
              Nothing needs your approval right now. We'll let you know the moment your team has something ready.
            </p>
          </div>
        ) : (
          <>
            {sort === "newest" && visible.length > 0 && (
              <Group label="Newest submissions" count={visible.length}>
                {visible.map((item) => (
                  <ApprovalCard key={item.id} item={item} onApprove={setConfirm} />
                ))}
              </Group>
            )}
            {sort === "due" && soon.length > 0 && (
              <Group label="Due soon" count={soon.length} due>
                {soon.map((item) => (
                  <ApprovalCard key={item.id} item={item} onApprove={setConfirm} />
                ))}
              </Group>
            )}
            {sort === "due" && later.length > 0 && (
              <Group label="Later" count={later.length}>
                {later.map((item) => (
                  <ApprovalCard key={item.id} item={item} onApprove={setConfirm} />
                ))}
              </Group>
            )}
            {visible.length === 0 && (
              <p className="py-10 text-center text-[13px] text-ink-3">No approvals in this category.</p>
            )}
            <div className="pt-2 text-center text-[13px] text-ink-3">
              You're all caught up on your approvals for now.
            </div>
          </>
        )}
      </div>

      <Dialog open={confirm != null} onOpenChange={(open) => !open && setConfirm(null)}>
        <DialogContent className="sm:max-w-[440px]">
          {confirm && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{confirmCopy(confirm)?.title ?? "Approve this item?"}</DialogTitle>
                <DialogDescription className="text-[13px] leading-relaxed">
                  {confirmCopy(confirm)?.body}
                </DialogDescription>
              </DialogHeader>
              <div className="rounded-xl border border-border bg-accent px-4 py-3 text-[13px]">
                <div className="font-display font-semibold">{confirm.title}</div>
                <div className="mt-0.5 text-ink-3">
                  {confirm.project} · #{confirm.order}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirm(null)} disabled={submitting}>
                  Cancel
                </Button>
                <Button className="gap-1.5 font-semibold" onClick={() => approve(confirm)} disabled={submitting}>
                  <Check className="size-4" strokeWidth={2.4} />
                  {submitting ? "Approving…" : "Approve"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

Approvals.layout = (page) => (
  <AppShell active="Approvals" crumb={["Approvals"]}>
    {page}
  </AppShell>
)
